import { useEffect, useMemo, useState } from "react";
import { Check, Loader as Loader2, Plus } from "lucide-react";
import { supabase, type Database } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useTags } from "@/hooks/useTags";
import { useContactCampaigns } from "@/hooks/useContactCampaigns";
import { useContactManagement } from "@/hooks/useContactManagement";
import { getDisplayName, formatWhatsAppJid } from "@/utils/contactUtils";
import { formatPauseRemainingPtBr, getAutomationUiState } from "@/utils/formatAutomationPause";
import { MobileSheet } from "./MobileSheet";
import { MobileSelect, MobileFieldLabel, mobileInputClass } from "./MobileSelect";
import { MobileTagChip } from "./MobileTagChip";

type AccountContactBase = Database["public"]["Tables"]["account_contacts"]["Row"];

export type ContactWithAssistant = AccountContactBase & {
  account_assistant_id?: string | null;
  assistant_id?: string | null;
  assistant_disabled_until?: string | null;
  assistant_is_active?: boolean | null;
};

interface MobileContactInfoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactWithAssistant | null;
  /** Chamado após qualquer alteração salva, para o pai recarregar o contato. */
  onContactUpdated: () => void;
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl border border-border bg-background p-4">
    <h3 className="mb-3 text-[13px] font-bold text-foreground">{title}</h3>
    {children}
  </section>
);

const Toggle = ({
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  ariaLabel: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${
      checked ? "bg-accent-blue" : "bg-border"
    }`}
  >
    <span
      className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 ${
        checked ? "translate-x-[22px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

/**
 * Conteúdo do painel de informações. Fica em um componente separado para que
 * os hooks de dados (assistentes, equipe, tags, campanhas) só sejam montados
 * quando o painel é realmente aberto.
 */
const ContactInfoContent = ({
  contact,
  onContactUpdated,
}: {
  contact: ContactWithAssistant;
  onContactUpdated: () => void;
}) => {
  const { user } = useAuth();
  const { assistants, loading: loadingAssistants } = useAssistants();
  const { members, loading: loadingMembers } = useTeam();
  const { tags: availableTags, fetchTags } = useTags();
  const { campaignHistory, loading: loadingCampaigns } = useContactCampaigns(contact.id);
  const contactManagement = useContactManagement();

  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [attributionId, setAttributionId] = useState<string | null>(null);
  const [chatStatus, setChatStatus] = useState<"aberto" | "concluido">("aberto");
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const [updatingAttribution, setUpdatingAttribution] = useState(false);
  const [remainingPause, setRemainingPause] = useState("");

  useEffect(() => {
    setAssistantId(contact.account_assistant_id || contact.assistant_id || null);
    setAttributionId(contact.attribution_id || null);
    setChatStatus(contact.status_chat === "concluido" ? "concluido" : "aberto");
    setContactTags(contact.tags ?? []);
  }, [contact]);

  const automationState = useMemo(
    () =>
      getAutomationUiState({
        assistant_is_active: contact.assistant_is_active,
        assistant_disabled_until: contact.assistant_disabled_until ?? null,
      }),
    [contact.assistant_is_active, contact.assistant_disabled_until]
  );

  // Contador do tempo restante quando a automação está pausada
  useEffect(() => {
    if (automationState !== "pausada" || !contact.assistant_disabled_until) {
      setRemainingPause("");
      return;
    }

    const tick = () => {
      const diff = new Date(contact.assistant_disabled_until!).getTime() - Date.now();
      setRemainingPause(diff > 0 ? formatPauseRemainingPtBr(diff) : "");
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [automationState, contact.assistant_disabled_until]);

  const campaignNames = useMemo(() => {
    const names = (campaignHistory ?? [])
      .map((entry) => entry.campaign_name)
      .filter((name): name is string => !!name && name.trim() !== "");
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [campaignHistory]);

  const tagColors = useMemo(
    () => Object.fromEntries(availableTags.map((tag) => [tag.name, tag.color])),
    [availableTags]
  );

  const allTagNames = useMemo(() => {
    const known = availableTags.filter((tag) => tag.is_active).map((tag) => tag.name);
    return [...new Set([...contactTags, ...known])];
  }, [availableTags, contactTags]);

  const canAssignOthers = user?.can_assign_contacts === true || user?.type === "agency_user";
  const lockedToOther =
    !canAssignOthers && !!contact.attribution_id && contact.attribution_id !== user?.auth_user_id;

  const handleAssistantChange = async (value: string) => {
    const next = value === "none" ? null : value;
    const ok = await contactManagement.assignAssistant(contact.id, next);
    if (ok) {
      setAssistantId(next);
      onContactUpdated();
    }
  };

  const handleAutomationToggle = async (checked: boolean) => {
    const ok = checked
      ? await contactManagement.enableAssistant(contact.id)
      : await contactManagement.disableAssistant(contact.id);
    if (ok) onContactUpdated();
  };

  const handleAttributionChange = async (value: string) => {
    if (!user || !supabase) return;
    const next = value === "none" ? null : value;
    if (!canAssignOthers && next && next !== user.auth_user_id) return;

    setUpdatingAttribution(true);
    try {
      const { error } = await supabase
        .from("account_contacts")
        .update({ attribution_id: next })
        .eq("id", contact.id)
        .eq("account_id", user.account_id);

      if (error) throw error;

      setAttributionId(next);
      onContactUpdated();
    } catch (err) {
      console.error("[MobileContactInfoSheet] Erro ao atualizar atribuição:", err);
    } finally {
      setUpdatingAttribution(false);
    }
  };

  const handleChatStatusChange = async (status: "aberto" | "concluido") => {
    if (status === chatStatus) return;
    const ok = await contactManagement.updateChatStatus(contact.id, status);
    if (ok) {
      setChatStatus(status);
      onContactUpdated();
    }
  };

  const handleToggleTag = async (tagName: string) => {
    const next = contactTags.includes(tagName)
      ? contactTags.filter((t) => t !== tagName)
      : [...contactTags, tagName];

    setContactTags(next);
    const ok = await contactManagement.setTags(contact.id, next);
    if (ok) {
      onContactUpdated();
    } else {
      setContactTags(contactTags);
    }
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim().toLowerCase();
    if (!name || creatingTag) return;

    setCreatingTag(true);
    try {
      // addTag também cadastra a tag em account_tags quando ela ainda não existe
      const ok = await contactManagement.addTag(contact.id, name);
      if (ok) {
        setContactTags([...new Set([...contactTags, name])]);
        setNewTagName("");
        await fetchTags();
        onContactUpdated();
      }
    } finally {
      setCreatingTag(false);
    }
  };

  const displayName = getDisplayName(contact);
  const phone = contact.phone || formatWhatsAppJid(contact.jid);
  const createdAt = contact.created_at
    ? new Date(contact.created_at).toLocaleDateString("pt-BR")
    : null;

  return (
    <div className="flex flex-col gap-3 pb-2">
      <Section title="Dados do contato">
        <dl className="flex flex-col gap-2 text-[14px]">
          <div className="flex items-start justify-between gap-3">
            <dt className="text-muted-foreground">Nome</dt>
            <dd className="min-w-0 truncate font-medium text-foreground">{displayName}</dd>
          </div>
          <div className="flex items-start justify-between gap-3">
            <dt className="text-muted-foreground">Telefone</dt>
            <dd className="min-w-0 truncate font-medium text-foreground">{phone}</dd>
          </div>
          {contact.email && (
            <div className="flex items-start justify-between gap-3">
              <dt className="text-muted-foreground">E-mail</dt>
              <dd className="min-w-0 truncate font-medium text-foreground">{contact.email}</dd>
            </div>
          )}
          {createdAt && (
            <div className="flex items-start justify-between gap-3">
              <dt className="text-muted-foreground">Criado em</dt>
              <dd className="font-medium text-foreground">{createdAt}</dd>
            </div>
          )}
        </dl>
      </Section>

      <Section title="Assistente">
        <div className="flex flex-col gap-3">
          <MobileSelect
            value={assistantId || "none"}
            onChange={handleAssistantChange}
            disabled={loadingAssistants || contactManagement.updating}
            ariaLabel="Assistente do contato"
          >
            <option value="none">Nenhum assistente</option>
            {assistants
              .filter((a) => a.is_active)
              .map((assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </option>
              ))}
          </MobileSelect>

          <div className="flex items-center justify-between gap-3 rounded-xl bg-card-muted px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-foreground">
                {automationState === "ligada" && "Automação ligada"}
                {automationState === "pausada" && "Automação pausada"}
                {automationState === "desligada" && "Automação desligada"}
              </p>
              <p className="text-[12px] text-muted-foreground">
                {automationState === "pausada" && remainingPause
                  ? `Retoma em ${remainingPause}`
                  : assistantId
                    ? "O assistente responde automaticamente"
                    : "Selecione um assistente para ativar"}
              </p>
            </div>
            <Toggle
              checked={automationState !== "desligada"}
              onChange={handleAutomationToggle}
              disabled={!assistantId || contactManagement.updating}
              ariaLabel="Ligar ou desligar a automação"
            />
          </div>
        </div>
      </Section>

      <Section title="Atendimento">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <MobileFieldLabel>Usuário atribuído</MobileFieldLabel>
            <MobileSelect
              value={attributionId || "none"}
              onChange={handleAttributionChange}
              disabled={loadingMembers || updatingAttribution || lockedToOther}
              ariaLabel="Usuário atribuído"
            >
              <option value="none">Nenhum usuário</option>
              {members
                .filter((m) => m.is_active && m.auth_user_id)
                .filter((m) => (canAssignOthers ? true : m.auth_user_id === user?.auth_user_id))
                .map((member) => (
                  <option key={member.auth_user_id} value={member.auth_user_id!}>
                    {member.name || member.email}
                  </option>
                ))}
            </MobileSelect>
            {lockedToOther && (
              <p className="text-[12px] text-amber-500">
                Contato atribuído a outro agente. Peça a um administrador para alterar.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <MobileFieldLabel>Status do chat</MobileFieldLabel>
            <div className="flex gap-2">
              {(["aberto", "concluido"] as const).map((status) => {
                const active = chatStatus === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleChatStatusChange(status)}
                    disabled={contactManagement.updating}
                    className={`flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border text-[14px] font-medium transition-colors ${
                      active
                        ? "border-accent-blue bg-accent-blue/10 text-accent-blue"
                        : "border-border bg-background text-muted-foreground active:bg-hover"
                    }`}
                  >
                    {active && <Check className="h-4 w-4" />}
                    {status === "aberto" ? "Aberto" : "Concluído"}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Tags">
        <div className="flex flex-col gap-3">
          {allTagNames.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {allTagNames.map((name) => (
                <MobileTagChip
                  key={name}
                  name={name}
                  color={tagColors[name]}
                  selected={contactTags.includes(name)}
                  disabled={contactManagement.updating}
                  onClick={() => handleToggleTag(name)}
                />
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-muted-foreground">Nenhuma tag criada ainda.</p>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="Criar nova tag"
              className={mobileInputClass}
            />
            <button
              type="button"
              onClick={handleCreateTag}
              disabled={!newTagName.trim() || creatingTag}
              aria-label="Criar tag"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-blue text-white transition-opacity active:opacity-80 disabled:opacity-40"
            >
              {creatingTag ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </Section>

      <Section title="Campanhas">
        {loadingCampaigns ? (
          <p className="text-[13px] text-muted-foreground">Carregando campanhas...</p>
        ) : campaignNames.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {campaignNames.map((name) => (
              <span
                key={name}
                className="rounded-full bg-accent-blue/10 px-2.5 py-1 text-[12px] font-medium capitalize text-accent-blue"
              >
                {name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[13px] text-muted-foreground">
            Este contato não está em nenhuma campanha ativa.
          </p>
        )}
      </Section>
    </div>
  );
};

export const MobileContactInfoSheet = ({
  open,
  onOpenChange,
  contact,
  onContactUpdated,
}: MobileContactInfoSheetProps) => {
  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title="Informações do contato">
      {open && contact && (
        <ContactInfoContent contact={contact} onContactUpdated={onContactUpdated} />
      )}
    </MobileSheet>
  );
};
